// Turns the CMS document into what the sections need, with content.js and
// copy.js as the fallbacks, so a section never has to ask whether the CMS was
// empty. The CMS always wins when it has a value (same `pick` as Home).

import { DAY, FAQ, HOW, STORY, WHO, fillVars, fitTrial, signupHref, trialCta, trialPhrase } from "../Home/content";
import { currencyLabel, opensEverything } from "../Home/pricing";
import { EMPTY_HOME_CONTENT } from "../../services/homeContentApi";
import { CONTACT_FALLBACK, HERO_FALLBACK, HERO_SECONDARY, OUTCOMES, PRICE_LABELS, SIGNUPS_CLOSED_CTA, TRUST } from "./copy";

const pick = (cms, fallback) => (cms && Object.keys(cms).length ? cms : fallback);
const clean = (v) => String(v ?? "").trim();
const fmt = (n) => Number(n || 0).toLocaleString("en-US");

export const telHref = (phone) => `tel:${String(phone).replace(/[^\d+]/g, "")}`;

// One plan on sale gives three cards (free month, monthly, yearly); several
// give one card each. A card whose number is unknown is dropped, not guessed.
// The grace period in a sentence: it is the owner's now, so it can be one day, or none.
const graceWords = (n) => (n === 1 ? "يوم واحد" : n === 2 ? "يومين" : n <= 10 ? `${n} أيام` : `${n} يوم`);
const fitGrace = (line, days) => {
    const n = Number(days);
    if (days == null || !String(line).includes("{graceDays}")) return line;
    if (n === 0) return "الاشتراك المدفوع لما يخلص: الحساب بيتقفل على طول.";
    return String(line).split("{graceDays} أيام").join(graceWords(n));
};

// The section's field words every card's button. The server already applied it to plan.cta;
// reading it here too is for the admin preview, which holds the form's text before any save.
const planCta = (pricing, plan) => clean(pricing.ctaText) || plan.ctaDefault || plan.cta || HOW.ctaText;

// The document holds only the labels someone rewrote; the rest stay copy.js's.
const priceLabels = (pricing = {}) => ({ ...PRICE_LABELS, ...(pricing.labels || {}) });

export function buildPriceCards(pricing = {}, signupsClosed = false) {
    const plans = Array.isArray(pricing.plans) ? pricing.plans : [];
    const currency = currencyLabel(pricing.currency);
    const L = priceLabels(pricing);
    const vars = { trialDays: pricing.trialDays };

    if (plans.length === 1) {
        const plan = plans[0];
        const monthly = Number(plan.monthly) || 0;
        const yearly = Number(plan.yearly) || 0;
        const saved = monthly > 0 && yearly > 0 ? Math.round((monthly * 12 - yearly) / monthly) : 0;
        const savedLabel = saved === 1 ? "شهر" : saved === 2 ? "شهرين" : `${saved} شهور`;
        const everything = opensEverything(plan, pricing.featureCatalog);
        const titled = (t) => (everything ? `${t}، ${L.everything}` : t);
        const cta = signupsClosed ? SIGNUPS_CLOSED_CTA : { text: fitTrial(planCta(pricing, plan), pricing.trialDays), href: signupHref(plan.ctaHref) };
        const trialTitle = fillVars(L.trialTitle, vars);

        return [
            trialTitle ? {
                id: "trial",
                kicker: trialPhrase(pricing.trialDays),
                title: trialTitle,
                capture: "board",
                cells: [
                    { label: L.duration, value: fillVars("{trialDays} يوم", vars) },
                    { label: L.afterwards, value: L.afterwardsValue },
                ],
                cta: signupsClosed ? SIGNUPS_CLOSED_CTA : { text: trialCta(pricing.trialDays), href: signupHref(plan.ctaHref) },
            } : null,
            monthly > 0 ? {
                id: "monthly",
                kicker: plan.name,
                title: titled(L.monthlyTitle),
                capture: "jobs",
                cells: [
                    { label: L.price, value: `${fmt(monthly)} ${currency}` },
                    { label: L.payment, value: L.paidMonthly },
                ],
                cta,
            } : null,
            yearly > 0 ? {
                id: "yearly",
                kicker: plan.name,
                title: titled(L.yearlyTitle),
                capture: "finance",
                cells: [
                    { label: L.price, value: `${fmt(yearly)} ${currency}` },
                    saved > 0
                        ? { label: L.saved, value: savedLabel }
                        : { label: L.payment, value: L.paidYearly },
                ],
                cta,
            } : null,
        ].filter(Boolean);
    }

    const captures = ["board", "jobs", "finance", "stock", "invoices"];
    return plans.map((plan, i) => ({
        id: plan.id || plan.name || String(i),
        kicker: plan.popular ? L.popular : plan.desc || "",
        title: plan.name,
        capture: captures[i % captures.length],
        cells: [
            { label: L.monthlyCell, value: `${fmt(plan.monthly)} ${currency}` },
            Number(plan.yearly) > 0
                ? { label: L.yearlyCell, value: `${fmt(plan.yearly)} ${currency}` }
                : { label: L.payment, value: L.paidMonthly },
        ],
        cta: signupsClosed ? SIGNUPS_CLOSED_CTA : { text: fitTrial(planCta(pricing, plan), pricing.trialDays), href: signupHref(plan.ctaHref) },
    }));
}

// The header and the phone menu. «السعر» is the highlighted pill.
export function buildNav({ hasPricing = true, hasTestimonials = false } = {}) {
    return [
        { id: "day", label: "يوم الريّس" },
        ...(hasPricing ? [{ id: "pricing", label: "السعر", pill: true }] : []),
        { id: "how", label: "إزاي تبدأ" },
        { id: "faq", label: "أسئلة" },
        ...(hasTestimonials ? [{ id: "testimonials", label: "آراء العملاء" }] : []),
        { id: "contact", label: "كلّمنا" },
    ];
}

export function buildLandingData(content) {
    const c = content || EMPTY_HOME_CONTENT;
    const pricing = c.pricing || {};
    const plans = Array.isArray(pricing.plans) ? pricing.plans : [];
    const vars = { trialDays: pricing.trialDays, graceDays: pricing.graceDays };
    const hasPricing = plans.length > 0;
    const testimonials = c.testimonials || {};
    const hasTestimonials = (testimonials.items || []).length > 0;

    // The contact heading falls back; an email or a phone never does. The
    // page shows only the ones the CMS really has.
    const contact = {
        ...c.contact,
        eyebrow: clean(c.contact?.eyebrow) || CONTACT_FALLBACK.eyebrow,
        titleAccent: clean(c.contact?.titleAccent) || CONTACT_FALLBACK.titleAccent,
        titleRest: c.contact?.titleRest || CONTACT_FALLBACK.titleRest,
        subtitle: clean(c.contact?.subtitle) || CONTACT_FALLBACK.subtitle,
    };
    const phone = clean(contact.phone);
    const email = clean(contact.email);

    // Every "start" button on the page, and where it goes once signups are closed.
    const signupsClosed = c.signupsOpen === false;
    const cta = signupsClosed
        ? SIGNUPS_CLOSED_CTA
        : { text: fitTrial(clean(c.hero?.primaryButton?.text) || HERO_FALLBACK.ctaText, pricing.trialDays), href: signupHref(c.hero?.primaryButton?.href) };

    const hero = {
        title: clean(c.hero?.title) || HERO_FALLBACK.title,
        titleAccent: clean(c.hero?.titleAccent) || HERO_FALLBACK.titleAccent,
        subtitle: clean(c.hero?.subtitle) || HERO_FALLBACK.subtitle,
        ctaText: cta.text,
        ctaHref: cta.href,
        image: clean(c.hero?.image),
    };

    // Hero's second button, by what exists.
    const secondary = phone
        ? { kind: "phone", label: HERO_SECONDARY.phone, href: telHref(phone) }
        : hasPricing
            ? { kind: "pricing", label: HERO_SECONDARY.pricing, href: "#pricing" }
            : { kind: "contact", label: HERO_SECONDARY.contact, href: "#contact" };

    // A line left blank in the CMS is how it is hidden, so blank counts as a value here.
    const trustCms = new Map((c.trust?.items || []).map((t) => [t.id, t.text]));
    const trust = TRUST
        .map((t) => ({ ...t, text: fillVars(trustCms.has(t.id) ? trustCms.get(t.id) : t.text, vars) }))
        .filter((t) => t.text && !(signupsClosed && t.id === "trial"));

    // The chapters describe optional modules too. Said plainly when the plan
    // on sale does not include them all; they are all open in the trial.
    const allOpen = plans.length > 0 && plans.every((p) => opensEverything(p, pricing.featureCatalog));
    const planNote = allOpen ? null : fitTrial("بعض الموديولز دي بتتفتح حسب الخطة — وكلها مفتوحة في الشهر المجاني.", pricing.trialDays);
    const howCopy = pick(c.how, HOW);
    const labels = priceLabels(pricing);
    const outcomeCms = new Map((c.outcomes?.items || []).map((o) => [o.id, o]));

    return {
        hero,
        secondary,
        trust,
        story: pick(c.story, STORY),
        day: pick(c.day, DAY),
        planNote,
        cta,
        how: {
            ...howCopy,
            ctaText: signupsClosed ? cta.text : fitTrial(howCopy.ctaText, pricing.trialDays),
            whatIf: (howCopy.whatIf || []).map((line) => fitGrace(fitTrial(line, pricing.trialDays), pricing.graceDays)),
        },
        who: pick(c.who, WHO),
        faq: pick(c.faq, FAQ),
        vars,
        pricing,
        priceCards: buildPriceCards(pricing, signupsClosed),
        finePrint: labels.finePrint,
        priceAside: labels.aside,
        outcomes: {
            title: clean(c.outcomes?.title) || OUTCOMES.title,
            items: OUTCOMES.items.map((item) => ({
                ...item,
                title: clean(outcomeCms.get(item.id)?.title) || item.title,
                line: clean(outcomeCms.get(item.id)?.line) || item.line,
            })),
        },
        // Each link appears once its page has something to say.
        legal: { privacy: !!clean(c.legal?.privacy?.body), terms: !!clean(c.legal?.terms?.body) },
        testimonials,
        hasTestimonials,
        hasPricing,
        contact,
        phone,
        email,
        social: c.social || {},
        nav: buildNav({ hasPricing, hasTestimonials }),
    };
}
